import { IResiStore, ListenerFn, UpdateFn } from "@base/resi/IResiStore";
import { Selector, Updater } from "@base/resi/Resi";
import { EqualsFunctionOption } from "@base/util/CompareUtil";
import { ImmHelper } from "@base/util/Immutable";

class _ResiRef<STORE_STATE extends object, REF_STATE extends object> implements IResiStore<REF_STATE> {
  private store: IResiStore<STORE_STATE>;
  private selector: Selector<STORE_STATE, REF_STATE>;
  private updater: Updater<STORE_STATE, REF_STATE>;
  public name: string;

  public constructor(store: IResiStore<STORE_STATE>, selector: Selector<STORE_STATE, REF_STATE>, updater: Updater<STORE_STATE, REF_STATE>) {
    this.name = "ref to " + store.name;
    this.store = store;
    this.selector = selector;
    this.updater = updater;
  }

  public get(): REF_STATE {
    const storeState = this.store.get();
    const refState = this.selector(storeState);
    return refState;
  }

  public set(stateOrFn: REF_STATE | UpdateFn<REF_STATE>): REF_STATE {
    if (typeof stateOrFn === "function") {
      const oldStoreState = this.store.get();
      const oldRefState = this.selector(oldStoreState);
      const newRefState = (stateOrFn as UpdateFn<REF_STATE>)(oldRefState);
      if (newRefState === oldRefState) return oldRefState;
      const newStoreState = this.updater(oldStoreState, newRefState);
      this.store.set(newStoreState);
      return newRefState;
    } else {
      const storeState = this.store.get();
      const newStoreState = this.updater(storeState, stateOrFn);
      this.store.set(newStoreState);
      return stateOrFn;
    }
  }

  public useSelector<R>(selector: (state: REF_STATE) => R, options?: { equalityFn?: EqualsFunctionOption; debugName?: string }): R {
    const combinedSelector = (state: STORE_STATE): R => {
      const sel1 = this.selector(state);
      const sel2 = selector(sel1);
      return sel2;
    };
    return this.store.useSelector(combinedSelector, options);
  }

  public subscribe(listenerFunction: ListenerFn<REF_STATE>, debugName?: string): () => void {
    let currentState = this.selector(this.store.get());
    return this.store.subscribe((state: STORE_STATE) => {
      const refState = this.selector(state);
      if (currentState !== refState) {
        listenerFunction(refState);
        currentState = refState;
      }
    }, debugName);
  }
}

function createUpdaterFromSelector<STORE_STATE extends object, REF_STATE>(selector: Selector<STORE_STATE, REF_STATE>): Updater<STORE_STATE, REF_STATE> {
  const path = ImmHelper.getPathFromSelector(selector);
  return (state: STORE_STATE, newVal: REF_STATE) => {
    return ImmHelper.setPropertyPath(state, path, newVal);
  };
}

export function createSubStore<STORE_STATE extends object, REF_STATE extends object>(store: IResiStore<STORE_STATE>, selector: Selector<STORE_STATE, REF_STATE>, updater?: Updater<STORE_STATE, REF_STATE>): IResiStore<REF_STATE> {
  if (!updater)
    // calculate update from selector...
    updater = createUpdaterFromSelector(selector);

  const t = new _ResiRef(store, selector, updater);
  return t as typeof t;
}
